import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import { Search as SearchIcon } from '@mui/icons-material';
import NewTaskButton from './NewTaskButton';
import UserSearchMenu from '../Common/UserSearchMenu';
import ContactSearchMenu from '../Common/ContactSearchMenu';
import { TASK_PRIORITY_OPTIONS, TASK_TYPE } from '../../models/TaskDTO';
import { useQuery } from '@tanstack/react-query';
import { LeadCrudService } from '../../api/generated/requests/services.gen';

interface TaskSearchBarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  priorityFilter: string;
  setPriorityFilter: (value: string) => void;
  taskTypeFilter: string;
  setTaskTypeFilter: (value: string) => void;
  userFilter: string;
  setUserFilter: (value: string) => void;
  clientFilter: number | undefined;
  setClientFilter: (value: number | undefined) => void;
  startDateFromFilter: Date | null;
  setStartDateFromFilter: (value: Date | null) => void;
  startDateToFilter: Date | null;
  setStartDateToFilter: (value: Date | null) => void;
  createdAtFromFilter: Date | null;
  setCreatedAtFromFilter: (value: Date | null) => void;
  createdAtToFilter: Date | null;
  setCreatedAtToFilter: (value: Date | null) => void;
  setPage: (value: number) => void;
  refreshTasks: () => Promise<void>;
  getUserFullName: (userId?: string) => string;
  domainUsers: any[] | undefined;
  loadingUsers: boolean;
}

const TaskSearchBar: React.FC<TaskSearchBarProps> = ({
  searchTerm,
  setSearchTerm,
  priorityFilter,
  setPriorityFilter,
  taskTypeFilter,
  setTaskTypeFilter,
  userFilter,
  setUserFilter,
  clientFilter,
  setClientFilter,
  startDateFromFilter,
  setStartDateFromFilter,
  startDateToFilter,
  setStartDateToFilter,
  createdAtFromFilter,
  setCreatedAtFromFilter,
  createdAtToFilter,
  setCreatedAtToFilter,
  setPage,
  refreshTasks,
  getUserFullName,
  domainUsers,
  loadingUsers
}) => {
  const [userFilterAnchorEl, setUserFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [clientFilterAnchorEl, setClientFilterAnchorEl] = useState<HTMLElement | null>(null);

  // Query to fetch client data when clientId is available
  const { data: clientData } = useQuery({
    queryKey: ['lead-contact', clientFilter],
    queryFn: () => LeadCrudService.getApiLeadContactsById({ id: clientFilter! }),
    enabled: !!clientFilter,
    staleTime: 60000, // Cache for 1 minute
  });

  // Function to get client display name
  const getClientDisplayName = (clientId: number | undefined): string => {
    if (!clientId) return 'Przypisany do klienta';
    
    if (clientData) {
      // Create display name from client data
      if (clientData.firstName && clientData.lastName) {
        return `${clientData.firstName} ${clientData.lastName}`;
      } else if (clientData.name) {
        return clientData.name;
      }
    }
    
    return `Klient ${clientId}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 3
      }}
    >
      {/* Pierwsza linia filtrów */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Nowe zadanie */}
        <Box sx={{ width: '160px' }}>
          <NewTaskButton refreshTasks={refreshTasks} />
        </Box>

        {/* Wyszukiwarka */}
        <TextField
          placeholder="Szukaj zadań po tytule lub opisie"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ pl: 1 }}>
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            flexGrow: 1,
            minWidth: '200px',
            fontWeight: 400,
            textTransform: 'none',
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              height: '36px',
              paddingLeft: '4px',
              overflow: 'hidden',
              backgroundColor: '#fff',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '999px',
            },
            '& .MuiInputBase-input': {
              paddingY: '8px',
              color: '#000',
              fontSize: '0.9rem',
            },
            '& input.MuiInputBase-input::placeholder': {
              color: '#666',
              opacity: 1,
              fontSize: '0.9rem',
            }
          }}
        />


        {/* Przypisany do użytkownika */}
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setUserFilterAnchorEl(e.currentTarget)}
          sx={{
            width: '160px',
            height: '36px',
            borderRadius: '999px',
            backgroundColor: '#fff',
            textTransform: 'none',
            fontSize: '0.8rem',
            justifyContent: 'space-between',
            paddingLeft: '12px',
            paddingRight: '8px',
            color: userFilter === '' ? 'text.secondary' : 'text.primary',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            }
          }}
          endIcon={<SearchIcon fontSize="small" />}
        >
          {userFilter === '' ? 'Przypisany do' : getUserFullName(userFilter)}
        </Button>

        <UserSearchMenu
          anchorEl={userFilterAnchorEl}
          open={Boolean(userFilterAnchorEl)}
          onClose={() => setUserFilterAnchorEl(null)}
          onSelectUser={(userId) => {
            setUserFilter(userId === undefined ? '' : userId);
            setPage(0);
            setUserFilterAnchorEl(null);
          }}
          users={domainUsers}
          isLoading={loadingUsers}
          currentUserId={userFilter === '' ? undefined : userFilter}
          getUserDisplayName={getUserFullName}
        />
      </Box>

      {/* Druga linia filtrów */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Typ zadania */}
        <FormControl
          size="small"
          sx={{
            width: '160px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              height: '36px',
              backgroundColor: '#fff',
              paddingLeft: '12px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '999px',
            },
            '& .MuiSelect-select': {
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              height: '36px',
              paddingY: '8px',
            }
          }}
        >
          <InputLabel
            id="task-type-filter-label"
            sx={{ fontSize: '0.8rem' }}
          >
            Typ zadania
          </InputLabel>
          <Select
            labelId="task-type-filter-label"
            value={taskTypeFilter}
            label="Typ zadania"
            onChange={(e) => {
              setTaskTypeFilter(e.target.value as string);
              setPage(0);
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {TASK_TYPE.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Priorytet */}
        <FormControl
          size="small"
          sx={{
            width: '160px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '999px',
              height: '36px',
              backgroundColor: '#fff',
              paddingLeft: '12px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '999px',
            },
            '& .MuiSelect-select': {
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              height: '36px',
              paddingY: '8px',
            }
          }}
        >
          <InputLabel
            id="priority-filter-label"
            sx={{ fontSize: '0.8rem' }}
          >
            Priorytet
          </InputLabel>
          <Select
            labelId="priority-filter-label"
            value={priorityFilter}
            label="Priorytet"
            onChange={(e) => {
              setPriorityFilter(e.target.value as string);
              setPage(0);
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {TASK_PRIORITY_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Termin od-do */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
          <DatePicker
            label="Termin od"
            value={startDateFromFilter}
            onChange={(date) => {
              setStartDateFromFilter(date);
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: '160px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '36px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '999px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    paddingY: '8px',
                  }
                }
              }
            }}
          />
          <DatePicker
            label="Termin do"
            value={startDateToFilter}
            onChange={(date) => {
              setStartDateToFilter(date);
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: '160px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '36px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '999px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    paddingY: '8px',
                  }
                }
              }
            }}
          />
        </LocalizationProvider>

        {/* Data utworzenia od-do */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
          <DatePicker
            label="Utworzono od"
            value={createdAtFromFilter}
            onChange={(date) => {
              setCreatedAtFromFilter(date);
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: '160px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '36px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '999px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    paddingY: '8px',
                  }
                }
              }
            }}
          />
          <DatePicker
            label="Utworzono do"
            value={createdAtToFilter}
            onChange={(date) => {
              setCreatedAtToFilter(date);
              setPage(0);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: '160px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '36px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '999px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    paddingY: '8px',
                  }
                }
              }
            }}
          />
        </LocalizationProvider>

          {/* Przypisany do klienta */}
          <Button
              variant="outlined"
              size="small"
              onClick={(e) => setClientFilterAnchorEl(e.currentTarget)}
              sx={{
                  width: '200px',
                  height: '36px',
                  borderRadius: '999px',
                  backgroundColor: '#fff',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  justifyContent: 'space-between',
                  paddingLeft: '12px',
                  paddingRight: '8px',
                  color: !clientFilter ? 'text.secondary' : 'text.primary',
                  '&:hover': {
                      backgroundColor: '#f5f5f5',
                  }
              }}
              endIcon={<SearchIcon fontSize="small" />}
          >
              {getClientDisplayName(clientFilter)}
          </Button>

          <ContactSearchMenu
              anchorEl={clientFilterAnchorEl}
              open={Boolean(clientFilterAnchorEl)}
              onClose={() => setClientFilterAnchorEl(null)}
              onSelectContact={(leadId) => {
                  setClientFilter(leadId);
                  setPage(0);
                  setClientFilterAnchorEl(null);
              }}
              currentContactId={clientFilter}
          />

      {/* Wyczyść filtry */}
      {(searchTerm || priorityFilter !== '' || taskTypeFilter !== '' || userFilter !== '' || clientFilter ||
        startDateFromFilter !== null || startDateToFilter !== null || createdAtFromFilter !== null || createdAtToFilter !== null) && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setSearchTerm('');
            setPriorityFilter('');
            setTaskTypeFilter('');
            setUserFilter('');
            setClientFilter(undefined);
            setStartDateFromFilter(null);
            setStartDateToFilter(null);
            setCreatedAtFromFilter(null);
            setCreatedAtToFilter(null);
            setPage(0);
          }}
          sx={{
            width: '160px',
            height: '36px',
            borderRadius: '999px',
            whiteSpace: 'nowrap',
            fontWeight: 400,
            textTransform: 'none',
            fontSize: '0.85rem'
          }}
        >
          Wyczyść filtry
        </Button>
      )}
    </Box>
    </Box>
  );
};

export default TaskSearchBar;
